import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { currentTenantId, fetchMyTenants } from '../api.js';

/**
 * Gate child routes on the caller actually having a tenant they
 * belong to (card 058).
 *
 * `currentTenantId()` always returns *something* — the build-time
 * default falls back to the demo UUID — so a fresh dashboard session
 * happily fires `/v1/admin/*` calls against a tenant the user has no
 * membership row on.  The API then returns "Tenant not found" and
 * every page paints a destructive red banner.
 *
 * Instead, this component asks the platform endpoint which tenants
 * the user can actually reach and only renders children once the
 * active tenant id matches one of them.  The loading state is a
 * neutral skeleton (NOT a red banner), and the "no tenant" state
 * is a friendly hand-off pointing back at the tenant switcher in
 * the sidebar.
 *
 * Card 050 ships a dedicated tenant-picker page; until then we send
 * the user back to / where the sidebar switcher lives.
 */
export function RequireTenant({ children }) {
  const { getToken } = useAuth();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'no-tenant'

  useEffect(() => {
    let alive = true;
    fetchMyTenants(getToken)
      .then(({ tenants }) => {
        if (!alive) return;
        const active = currentTenantId();
        const hit = Array.isArray(tenants)
          ? tenants.find((t) => t.tenant_id === active)
          : null;
        setStatus(hit ? 'ok' : 'no-tenant');
      })
      .catch(() => {
        // Platform endpoint failed (network blip, 401 during boot).
        // Render the neutral empty state — better than a red banner.
        if (alive) setStatus('no-tenant');
      });
    return () => { alive = false; };
  }, [getToken]);

  if (status === 'loading') {
    return (
      <div className="page">
        <div className="require-tenant-skeleton" role="status" aria-label="Loading tenant">
          <div className="require-tenant-skeleton__bar require-tenant-skeleton__bar--title" />
          <div className="require-tenant-skeleton__bar require-tenant-skeleton__bar--sub" />
          <div className="require-tenant-skeleton__panel" />
        </div>
        <style>{REQUIRE_TENANT_CSS}</style>
      </div>
    );
  }

  if (status === 'no-tenant') {
    return (
      <div className="page">
        <div className="require-tenant-empty">
          <div className="require-tenant-empty__title">Pick a tenant to see this page</div>
          <p className="require-tenant-empty__body">
            This page needs an active tenant before it can load.
            Use the tenant switcher at the top of the sidebar to
            pick one, then come back.
          </p>
          <Link to="/" className="btn btn--secondary">
            Back to home
          </Link>
        </div>
        <style>{REQUIRE_TENANT_CSS}</style>
      </div>
    );
  }

  return children;
}

const REQUIRE_TENANT_CSS = `
  .require-tenant-skeleton {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 8px 0;
  }
  .require-tenant-skeleton__bar {
    background: var(--surface-2, #f1f5f9);
    border-radius: var(--r-md, 8px);
    height: 16px;
  }
  .require-tenant-skeleton__bar--title { width: 220px; height: 28px; }
  .require-tenant-skeleton__bar--sub   { width: 360px; }
  .require-tenant-skeleton__panel {
    background: var(--surface-2, #f1f5f9);
    border-radius: var(--r-md, 8px);
    height: 240px;
    margin-top: 8px;
  }
  @keyframes require-tenant-pulse {
    0%   { opacity: 0.55; }
    50%  { opacity: 1;    }
    100% { opacity: 0.55; }
  }
  .require-tenant-skeleton__bar,
  .require-tenant-skeleton__panel {
    animation: require-tenant-pulse 1.4s ease-in-out infinite;
  }

  .require-tenant-empty {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-radius: var(--r-md, 10px);
    padding: 28px 24px;
    max-width: 520px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .require-tenant-empty__title {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-1, #0f172a);
  }
  .require-tenant-empty__body {
    color: var(--text-2, #334155);
    font-size: 13.5px;
    line-height: 1.55;
    margin: 0;
  }
  .require-tenant-empty .btn {
    align-self: flex-start;
    margin-top: 4px;
  }
`;
